import { useState } from "react";
import {
  DataTable,
  DataTableColumnHeader,
  Button,
  Tag,
  Checkbox,
  SearchInput,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  InputField,
  Textarea,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Popover,
  PopoverTrigger,
  PopoverContent,
  CheckboxWithLabel,
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from "@jem-hr/jem-ui";
import { ColumnDef } from "@tanstack/react-table";
import {
  Upload,
  RefreshCw,
  Plus,
  Eye,
  Pencil,
  Trash2,
  ChevronDown,
  XCircle,
  CheckCircle2,
  CloudDownload,
  History,
  Users as UsersIcon,
  Tag as TagIcon,
} from "lucide-react";

// ─── Employee Picker ─────────────────────────────────────────────────────────

function EmployeePicker({
  employees,
  selected,
  onApply,
}: {
  employees: { id: string; label: string }[];
  selected: string[];
  onApply: (ids: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState<string[]>(selected);
  const [search, setSearch] = useState("");

  const filtered = employees.filter((e) =>
    e.label.toLowerCase().includes(search.toLowerCase())
  );

  const allSelected = employees.length > 0 && pending.length === employees.length;

  const handleOpen = (isOpen: boolean) => {
    if (isOpen) {
      setPending(selected);
      setSearch("");
    }
    setOpen(isOpen);
  };

  const handleToggle = (id: string, checked: boolean) => {
    setPending((prev) =>
      checked ? [...prev, id] : prev.filter((p) => p !== id)
    );
  };

  const handleSelectAll = (checked: boolean) => {
    setPending(checked ? employees.map((e) => e.id) : []);
  };

  const handleApply = () => {
    onApply(pending);
    setOpen(false);
  };

  const count = selected.length;

  return (
    <Popover open={open} onOpenChange={handleOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="flex items-center gap-xxs w-full min-h-[46px] rounded-lg border border-greyscale-border bg-white px-sm py-xxs text-left"
        >
          <div className="flex items-center gap-xxs flex-1">
            <UsersIcon size={16} className="text-greyscale-text-caption shrink-0" />
            {count > 0 ? (
              <span className="text-sm text-greyscale-text-title font-semibold">
                {count === employees.length
                  ? `All employees selected (${count})`
                  : `${count} employee${count !== 1 ? "s" : ""} selected`}
              </span>
            ) : (
              <span className="text-sm text-primary-navy-300">
                Select employees...
              </span>
            )}
          </div>
          <ChevronDown size={16} className="text-greyscale-text-caption shrink-0" />
        </button>
      </PopoverTrigger>

      <PopoverContent align="start" className="w-[382px] p-none">
        <div className="p-xs">
          <SearchInput
            placeholder="Search by name or employee number..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onClear={() => setSearch("")}
          />
        </div>

        {/* Select All */}
        <div className="px-xs py-xxs border-b border-greyscale-border">
          <CheckboxWithLabel
            label={`Select All (${employees.length})`}
            checked={allSelected}
            onCheckedChange={(c) => handleSelectAll(c as boolean)}
          />
        </div>

        <div className="max-h-[240px] overflow-auto px-xs">
          {filtered.map((emp) => (
            <div key={emp.id} className="py-xxs">
              <CheckboxWithLabel
                label={emp.label}
                checked={pending.includes(emp.id)}
                onCheckedChange={(c) => handleToggle(emp.id, c as boolean)}
              />
            </div>
          ))}
          {filtered.length === 0 && (
            <p className="text-sm text-greyscale-text-caption py-xxs">
              No employees found.
            </p>
          )}
        </div>

        <div className="p-xs">
          <Button variant="outline" onClick={handleApply} className="w-full">
            Apply
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

// ─── Types ───────────────────────────────────────────────────────────────────

interface Employee {
  id: string;
  employeeNo: string;
  fullName: string;
  dateAdded: string;
  mobileNumber: string;
  tags: string[] | null;
  status: "ACTIVE" | "SUSPENDED";
}

interface TagRow {
  id: string;
  tagName: string;
  category: string;
  employees: number;
  createdBy: string;
  dateCreated: string;
}

interface Segment {
  id: string;
  segmentName: string;
  description: string;
  numEmployees: number;
  numRules: number;
  status: "Currently In Use" | "Not In Use";
}

interface ImportRow {
  id: string;
  uploadedBy: string;
  dateUploaded: string;
  employeesToCreate: number;
  employeesToUpdate: number;
  employeesCreated: number;
  employeesUpdated: number;
  failedRows: number;
  status: "COMPLETED" | "CANCELLED" | "FAILED";
}

interface Rule {
  id: string;
  field: string;
  rule: string;
  value: string;
}

let nextRuleId = 3;

// ─── Users data ──────────────────────────────────────────────────────────────

const usersData: Employee[] = [
  { id: "1", employeeNo: "14", fullName: "Abigail M.", dateAdded: "07/09/22", mobileNumber: "27662323996", tags: ["General"], status: "ACTIVE" },
  { id: "2", employeeNo: "23467", fullName: "Abongile M.", dateAdded: "17/10/22", mobileNumber: "27717376586", tags: ["General"], status: "ACTIVE" },
  { id: "3", employeeNo: "12345", fullName: "Adam D.", dateAdded: "11/07/23", mobileNumber: "27842507787", tags: ["Retail"], status: "ACTIVE" },
  { id: "4", employeeNo: "12345", fullName: "Adam D.", dateAdded: "20/06/25", mobileNumber: "27842507700", tags: ["Retail"], status: "ACTIVE" },
  { id: "5", employeeNo: "5678901", fullName: "Adam D.", dateAdded: "28/08/20", mobileNumber: "27842507733", tags: ["Warehouse"], status: "ACTIVE" },
  { id: "6", employeeNo: "GK62DKX", fullName: "Alana K.", dateAdded: "18/01/23", mobileNumber: "97297054730", tags: null, status: "SUSPENDED" },
  { id: "7", employeeNo: "WI39XKL", fullName: "Alene K.", dateAdded: "18/01/23", mobileNumber: "97104495021", tags: null, status: "SUSPENDED" },
  { id: "8", employeeNo: "573821918", fullName: "Alexandra B.", dateAdded: "18/04/24", mobileNumber: "27636457614", tags: ["Head Office"], status: "ACTIVE" },
];

const usersColumns: ColumnDef<Employee>[] = [
  {
    id: "select",
    header: ({ table }) => (
      <Checkbox
        checked={table.getIsAllPageRowsSelected()}
        onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
        aria-label="Select all"
      />
    ),
    cell: ({ row }) => (
      <Checkbox
        checked={row.getIsSelected()}
        onCheckedChange={(value) => row.toggleSelected(!!value)}
        aria-label="Select row"
      />
    ),
    enableSorting: false,
    enableHiding: false,
  },
  {
    accessorKey: "employeeNo",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Employee No" />
    ),
  },
  {
    accessorKey: "fullName",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Full name" />
    ),
  },
  {
    accessorKey: "dateAdded",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Date added" />
    ),
  },
  {
    accessorKey: "mobileNumber",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Mobile Number" />
    ),
  },
  {
    accessorKey: "tags",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Tags" />
    ),
    cell: ({ row }) => {
      const tags = row.original.tags;
      if (!tags || tags.length === 0) {
        return <span className="text-sm text-greyscale-text-caption">None</span>;
      }
      return (
        <button
          type="button"
          className="flex items-center gap-xxs rounded-lg border border-greyscale-border px-sm py-xxxs text-sm text-greyscale-text-body"
        >
          View all
          <ChevronDown size={14} />
        </button>
      );
    },
  },
  {
    accessorKey: "status",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Status" />
    ),
    cell: ({ row }) => (
      <Tag variant={row.original.status === "ACTIVE" ? "success" : "neutral"}>
        {row.original.status}
      </Tag>
    ),
  },
];

// ─── Tags data ───────────────────────────────────────────────────────────────

const tagsData: TagRow[] = [
  { id: "1", tagName: "Adam Test", category: "Adam Test", employees: 1, createdBy: "Adam D.", dateCreated: "15/01/26" },
  { id: "2", tagName: "Abo Tag", category: "All", employees: 1, createdBy: "Abongile M.", dateCreated: "20/11/25" },
  { id: "3", tagName: "Adams", category: "All", employees: 3, createdBy: "Adam D.", dateCreated: "05/09/25" },
  { id: "4", tagName: "Alex test", category: "All", employees: 1, createdBy: "Alexandra B.", dateCreated: "22/04/25" },
  { id: "5", tagName: "BK Takealot Send", category: "All", employees: 1, createdBy: "Kirsty R.", dateCreated: "10/03/25" },
  { id: "6", tagName: "Hearing Notice", category: "All", employees: 1, createdBy: "Francis Z.", dateCreated: "28/02/25" },
  { id: "7", tagName: "Ian's Tag", category: "All", employees: 1, createdBy: "Ian S.", dateCreated: "14/01/25" },
  { id: "8", tagName: "Jem Team", category: "All", employees: 13, createdBy: "Alexandra B.", dateCreated: "01/09/24" },
  { id: "9", tagName: "Patrick Test", category: "All", employees: 3, createdBy: "Patrick M.", dateCreated: "18/08/24" },
];

const tagsColumns: ColumnDef<TagRow>[] = [
  {
    id: "select",
    header: ({ table }) => (
      <Checkbox
        checked={table.getIsAllPageRowsSelected()}
        onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
        aria-label="Select all"
      />
    ),
    cell: ({ row }) => (
      <Checkbox
        checked={row.getIsSelected()}
        onCheckedChange={(value) => row.toggleSelected(!!value)}
        aria-label="Select row"
      />
    ),
    enableSorting: false,
    enableHiding: false,
  },
  {
    accessorKey: "tagName",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Tag name" />
    ),
    cell: ({ row }) => (
      <span className="text-sm font-semibold text-greyscale-text-title">
        {row.original.tagName}
      </span>
    ),
  },
  {
    accessorKey: "category",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Category" />
    ),
    cell: ({ row }) => (
      <Tag variant="pink">{row.original.category}</Tag>
    ),
  },
  {
    accessorKey: "employees",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Employees" />
    ),
    cell: ({ row }) => (
      <span className="inline-flex items-center gap-xxxs text-sm text-greyscale-text-body">
        <UsersIcon size={14} className="text-greyscale-text-caption" />
        <Tag variant="success">{row.original.employees}</Tag>
      </span>
    ),
  },
  {
    accessorKey: "createdBy",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Created by" />
    ),
  },
  {
    accessorKey: "dateCreated",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Date created" />
    ),
  },
  {
    id: "actions",
    cell: () => (
      <TooltipProvider delayDuration={300}>
        <div className="flex items-center gap-xxs">
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                className="text-greyscale-text-caption hover:text-greyscale-text-title"
              >
                <Eye size={16} />
              </button>
            </TooltipTrigger>
            <TooltipContent variant="dark">View</TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                className="text-greyscale-text-caption hover:text-greyscale-text-title"
              >
                <Pencil size={16} />
              </button>
            </TooltipTrigger>
            <TooltipContent variant="dark">Edit</TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                className="text-greyscale-text-caption hover:text-red-500"
              >
                <Trash2 size={16} />
              </button>
            </TooltipTrigger>
            <TooltipContent variant="dark">Delete</TooltipContent>
          </Tooltip>
        </div>
      </TooltipProvider>
    ),
  },
];

// ─── Segmentation data ───────────────────────────────────────────────────────

const segmentationData: Segment[] = [
  { id: "1", segmentName: "Test", description: "Amber's Test", numEmployees: 0, numRules: 1, status: "Not In Use" },
  { id: "2", segmentName: "Salary <R5000", description: "Salary <R5000", numEmployees: 44, numRules: 1, status: "Currently In Use" },
  { id: "3", segmentName: "Test pay cycle block", description: "Test pay cycle block", numEmployees: 1, numRules: 1, status: "Currently In Use" },
  { id: "4", segmentName: "R0 employees", description: "All employees with R0", numEmployees: 28, numRules: 1, status: "Currently In Use" },
  { id: "5", segmentName: "R0 Salary", description: "All employees with R0 salar...", numEmployees: 28, numRules: 1, status: "Currently In Use" },
  { id: "6", segmentName: "Rodrigue", description: "Block rodrigue", numEmployees: 1, numRules: 1, status: "Currently In Use" },
];

const segmentationColumns: ColumnDef<Segment>[] = [
  {
    accessorKey: "segmentName",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Segment Name" />
    ),
    cell: ({ row }) => (
      <span className="text-sm font-semibold text-greyscale-text-title">
        {row.original.segmentName}
      </span>
    ),
  },
  {
    accessorKey: "description",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Description" />
    ),
    cell: ({ row }) => (
      <span className="text-sm text-greyscale-text-caption">
        {row.original.description}
      </span>
    ),
  },
  {
    accessorKey: "numEmployees",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="No. of Employees" />
    ),
  },
  {
    accessorKey: "numRules",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="No. of Rules" />
    ),
  },
  {
    accessorKey: "status",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Status" />
    ),
    cell: ({ row }) => (
      <Tag
        variant={
          row.original.status === "Currently In Use" ? "success" : "neutral"
        }
      >
        {row.original.status}
      </Tag>
    ),
  },
  {
    id: "actions",
    cell: () => (
      <TooltipProvider delayDuration={300}>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              className="text-greyscale-text-caption hover:text-greyscale-text-title"
            >
              <Eye size={16} />
            </button>
          </TooltipTrigger>
          <TooltipContent variant="dark">View</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    ),
  },
];

// ─── Imports data ────────────────────────────────────────────────────────────

const importsData: ImportRow[] = [
  { id: "1", uploadedBy: "Richard O.", dateUploaded: "09/06/2025 12:07", employeesToCreate: 0, employeesToUpdate: 2, employeesCreated: 0, employeesUpdated: 2, failedRows: 0, status: "COMPLETED" },
  { id: "2", uploadedBy: "Richard O.", dateUploaded: "20/05/2025 16:28", employeesToCreate: 4, employeesToUpdate: 0, employeesCreated: 4, employeesUpdated: 0, failedRows: 0, status: "COMPLETED" },
  { id: "3", uploadedBy: "Kaeleigh S.", dateUploaded: "10/12/2024 09:27", employeesToCreate: 1, employeesToUpdate: 0, employeesCreated: 0, employeesUpdated: 0, failedRows: 0, status: "CANCELLED" },
  { id: "4", uploadedBy: "Ezra J.", dateUploaded: "20/08/2024 15:28", employeesToCreate: 0, employeesToUpdate: 0, employeesCreated: 0, employeesUpdated: 0, failedRows: 0, status: "FAILED" },
  { id: "5", uploadedBy: "Patrick M.", dateUploaded: "20/08/2024 15:22", employeesToCreate: 0, employeesToUpdate: 1, employeesCreated: 0, employeesUpdated: 1, failedRows: 0, status: "COMPLETED" },
  { id: "6", uploadedBy: "Ezra J.", dateUploaded: "20/08/2024 15:13", employeesToCreate: 0, employeesToUpdate: 0, employeesCreated: 0, employeesUpdated: 0, failedRows: 0, status: "FAILED" },
  { id: "7", uploadedBy: "Patrick M.", dateUploaded: "20/08/2024 14:45", employeesToCreate: 0, employeesToUpdate: 1, employeesCreated: 0, employeesUpdated: 1, failedRows: 0, status: "COMPLETED" },
  { id: "8", uploadedBy: "Ezra J.", dateUploaded: "08/08/2024 12:16", employeesToCreate: 0, employeesToUpdate: 1, employeesCreated: 0, employeesUpdated: 1, failedRows: 0, status: "COMPLETED" },
];

const importsColumns: ColumnDef<ImportRow>[] = [
  {
    id: "select",
    header: ({ table }) => (
      <Checkbox
        checked={table.getIsAllPageRowsSelected()}
        onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
        aria-label="Select all"
      />
    ),
    cell: ({ row }) => (
      <Checkbox
        checked={row.getIsSelected()}
        onCheckedChange={(value) => row.toggleSelected(!!value)}
        aria-label="Select row"
      />
    ),
    enableSorting: false,
    enableHiding: false,
  },
  {
    accessorKey: "uploadedBy",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Uploaded by" />
    ),
  },
  {
    accessorKey: "dateUploaded",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Date uploaded" />
    ),
  },
  {
    accessorKey: "employeesToCreate",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Employees to create" />
    ),
  },
  {
    accessorKey: "employeesToUpdate",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Employees to update" />
    ),
  },
  {
    accessorKey: "employeesCreated",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Employees created" />
    ),
  },
  {
    accessorKey: "employeesUpdated",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Employees updated" />
    ),
  },
  {
    accessorKey: "failedRows",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Failed rows" />
    ),
  },
  {
    accessorKey: "status",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Status" />
    ),
    cell: ({ row }) => {
      const s = row.original.status;
      const variant = s === "COMPLETED" ? "success" : s === "FAILED" ? "failed" : "neutral";
      return <Tag variant={variant}>{s}</Tag>;
    },
  },
  {
    id: "actions",
    cell: () => (
      <TooltipProvider delayDuration={300}>
        <div className="flex items-center gap-xxs">
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                className="text-greyscale-text-caption hover:text-red-500"
              >
                <XCircle size={16} />
              </button>
            </TooltipTrigger>
            <TooltipContent variant="dark">Cancel</TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                className="text-greyscale-text-caption hover:text-greyscale-text-title"
              >
                <CloudDownload size={16} />
              </button>
            </TooltipTrigger>
            <TooltipContent variant="dark">Download</TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                className="text-greyscale-text-caption hover:text-green-600"
              >
                <CheckCircle2 size={16} />
              </button>
            </TooltipTrigger>
            <TooltipContent variant="dark">Approve</TooltipContent>
          </Tooltip>
        </div>
      </TooltipProvider>
    ),
  },
];

// ─── Component ───────────────────────────────────────────────────────────────

export function Employees() {
  const [activeTab, setActiveTab] = useState("users");
  const [search, setSearch] = useState("");
  const [showTagForm, setShowTagForm] = useState(false);
  const [tags, setTags] = useState<TagRow[]>(tagsData);
  const [newTagName, setNewTagName] = useState("");
  const [newTagCategory, setNewTagCategory] = useState("");
  const [customCategory, setCustomCategory] = useState("");
  const [newTagDescription, setNewTagDescription] = useState("");
  const [newTagEmployees, setNewTagEmployees] = useState<string[]>([]);
  const [showSegmentForm, setShowSegmentForm] = useState(false);
  const [matchType, setMatchType] = useState("ANY");
  const [rules, setRules] = useState<Rule[]>([
    { id: "1", field: "", rule: "", value: "" },
    { id: "2", field: "", rule: "", value: "" },
  ]);

  const resetTagForm = () => {
    setShowTagForm(false);
    setNewTagName("");
    setNewTagCategory("");
    setCustomCategory("");
    setNewTagDescription("");
    setNewTagEmployees([]);
  };

  const saveTag = () => {
    if (!newTagName.trim()) return;
    const category =
      newTagCategory === "Custom" && customCategory.trim()
        ? customCategory.trim()
        : newTagCategory || "All";
    const today = new Date();
    const dateStr = `${String(today.getDate()).padStart(2, "0")}/${String(today.getMonth() + 1).padStart(2, "0")}/${String(today.getFullYear()).slice(2)}`;
    setTags((prev) => [
      {
        id: String(prev.length + 1),
        tagName: newTagName.trim(),
        category,
        employees: newTagEmployees.length,
        createdBy: "You",
        dateCreated: dateStr,
      },
      ...prev,
    ]);
    resetTagForm();
  };

  const resetSegmentForm = () => {
    setShowSegmentForm(false);
    setMatchType("ANY");
    setRules([
      { id: "1", field: "", rule: "", value: "" },
      { id: "2", field: "", rule: "", value: "" },
    ]);
  };

  const addRule = () => {
    setRules((prev) => [
      ...prev,
      { id: String(nextRuleId++), field: "", rule: "", value: "" },
    ]);
  };

  const removeRule = (id: string) => {
    if (rules.length <= 1) return;
    setRules((prev) => prev.filter((r) => r.id !== id));
  };

  const updateRule = (id: string, updates: Partial<Rule>) => {
    setRules((prev) =>
      prev.map((r) => (r.id === id ? { ...r, ...updates } : r))
    );
  };

  return (
    <div>
      {/* Header */}
      <div
        className="flex items-center justify-between -mx-xl -mt-lg px-xl py-lg mb-md"
        style={{ backgroundColor: "rgba(237,244,251,0.5)" }}
      >
        <div>
          <h1 className="font-heading text-2xl font-semibold leading-36 text-greyscale-text-title">
            Employees
          </h1>
          <p className="py-[6px] text-sm font-regular leading-18 text-greyscale-text-body">
            Manage your employees, tags, segments, and data imports.
          </p>
        </div>
        <div className="flex items-center gap-xxs">
          {(activeTab === "users" || activeTab === "imports") && (
            <>
              <Button leftIcon={<Upload size={16} />}>Import Data</Button>
              <Button leftIcon={<RefreshCw size={16} />}>
                Bulk Update Status
              </Button>
            </>
          )}
          {activeTab === "tags" && !showTagForm && (
            <Button
              leftIcon={<Plus size={16} />}
              onClick={() => setShowTagForm(true)}
            >
              Create Tag
            </Button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList variant="line" className="w-full justify-start">
          <TabsTrigger value="users" variant="line">
            Users
          </TabsTrigger>
          <TabsTrigger value="tags" variant="line">
            Tags
          </TabsTrigger>
          <TabsTrigger value="segmentation" variant="line">
            Segmentation
          </TabsTrigger>
          <TabsTrigger value="imports" variant="line">
            Imports
          </TabsTrigger>
        </TabsList>

        {/* ── Users tab ── */}
        <TabsContent value="users">
          <div className="flex items-center gap-xxs mt-md mb-md">
            <div className="flex-1">
              <SearchInput
                placeholder="search..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onClear={() => setSearch("")}
              />
            </div>
            <Button>Search</Button>
          </div>
          <DataTable
            columns={usersColumns}
            data={usersData}
            showPagination
            showToolbar={false}
          />
        </TabsContent>

        {/* ── Tags tab ── */}
        <TabsContent value="tags">
          {showTagForm ? (
            <div className="mt-md">
              <div className="max-w-[850px]">
                <p className="text-base font-semibold text-greyscale-text-title mb-md">
                  Create a new tag
                </p>

                <div className="flex flex-col gap-md">
                  {/* Tag name */}
                  <InputField
                    label="Tag name"
                    required
                    placeholder="e.g. Head Office, Warehouse Staff, Remote..."
                    value={newTagName}
                    onChange={(e) => setNewTagName(e.target.value)}
                  />

                  {/* Category */}
                  <div className="flex flex-col gap-xxxs">
                    <div>
                      <p className="text-sm font-semibold leading-18 text-greyscale-text-title">
                        Category <span className="text-red-500">*</span>
                      </p>
                      <p className="text-sm font-regular leading-18 text-greyscale-text-body">
                        Group this tag under a category for easier filtering
                      </p>
                    </div>
                    <Select
                      value={newTagCategory}
                      onValueChange={(v) => {
                        setNewTagCategory(v);
                        if (v !== "Custom") setCustomCategory("");
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="All">All</SelectItem>
                        <SelectItem value="Department">Department</SelectItem>
                        <SelectItem value="Location">Location</SelectItem>
                        <SelectItem value="Role">Role</SelectItem>
                        <SelectItem value="Custom">Custom</SelectItem>
                      </SelectContent>
                    </Select>
                    {newTagCategory === "Custom" && (
                      <InputField
                        placeholder="Enter custom category name"
                        value={customCategory}
                        onChange={(e) => setCustomCategory(e.target.value)}
                        className="mt-xxs"
                      />
                    )}
                  </div>

                  {/* Description */}
                  <div className="flex flex-col gap-xxxs">
                    <div>
                      <p className="text-sm font-semibold leading-18 text-greyscale-text-title">
                        Description
                      </p>
                      <p className="text-sm font-regular leading-18 text-greyscale-text-body">
                        Add an optional description for this tag
                      </p>
                    </div>
                    <Textarea
                      placeholder="e.g. All employees based at the head office"
                      className="min-h-[80px]"
                      value={newTagDescription}
                      onChange={(e) => setNewTagDescription(e.target.value)}
                    />
                  </div>

                  {/* Select employees */}
                  <div className="flex flex-col gap-xxxs">
                    <div>
                      <p className="text-sm font-semibold leading-18 text-greyscale-text-title">
                        Assign employees{" "}
                        <span className="text-red-500">*</span>
                      </p>
                      <p className="text-sm font-regular leading-18 text-greyscale-text-body">
                        Select the employees that belong to this tag
                      </p>
                    </div>
                    <EmployeePicker
                      employees={usersData.map((u) => ({
                        id: u.id,
                        label: `${u.fullName} (${u.employeeNo})`,
                      }))}
                      selected={newTagEmployees}
                      onApply={setNewTagEmployees}
                    />
                  </div>

                  {/* Info hint */}
                  <div className="flex items-center gap-xxs rounded-lg bg-neutral-50 px-md py-sm">
                    <TagIcon
                      size={16}
                      className="text-greyscale-text-caption"
                    />
                    <span className="text-sm text-greyscale-text-caption">
                      Employees can belong to multiple tags. You can update
                      assignments later.
                    </span>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="border-t border-greyscale-border mt-lg" />
              <div className="flex items-center justify-between py-lg">
                <Button variant="outline" onClick={resetTagForm}>
                  Go Back
                </Button>
                <Button onClick={saveTag}>Save Tag</Button>
              </div>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-xxs mt-md mb-md">
                <div className="flex-1 max-w-[500px]">
                  <SearchInput
                    placeholder="Search tags..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onClear={() => setSearch("")}
                  />
                </div>
                <Button>Search</Button>
              </div>
              <DataTable
                columns={tagsColumns}
                data={tags}
                showPagination
                showToolbar={false}
              />
            </>
          )}
        </TabsContent>

        {/* ── Segmentation tab ── */}
        <TabsContent value="segmentation">
          {showSegmentForm ? (
            <div className="mt-md">
              <div className="max-w-[850px]">
                {/* Form heading */}
                <p className="text-base font-semibold text-greyscale-text-title mb-md">
                  Create a new segment
                </p>

                <div className="flex flex-col gap-md">
                  {/* Segment name */}
                  <InputField
                    label="Segment name"
                    required
                    placeholder="e.g Sales Team, Remote Workers etc..."
                  />

                  {/* Description */}
                  <div className="flex flex-col gap-xxxs">
                    <div>
                      <p className="text-sm font-semibold leading-18 text-greyscale-text-title">
                        Description <span className="text-red-500">*</span>
                      </p>
                    </div>
                    <Textarea
                      placeholder="Enter a description for this segment"
                      className="min-h-[115px]"
                    />
                  </div>

                  {/* Rules criteria */}
                  <div className="flex flex-col gap-xxxs">
                    <div>
                      <p className="text-sm font-semibold leading-18 text-greyscale-text-title">
                        Rules criteria
                      </p>
                      <p className="text-sm font-regular leading-18 text-greyscale-text-body">
                        Set up all your rules – add more information here if
                        needed about how to set the rules
                      </p>
                    </div>

                    {/* Match type */}
                    <div className="flex items-center gap-xxs mt-xxs">
                      <span className="text-sm font-semibold text-greyscale-text-title">
                        Match
                      </span>
                      <Select
                        value={matchType}
                        onValueChange={setMatchType}
                      >
                        <SelectTrigger className="w-[100px]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="ANY">ANY</SelectItem>
                          <SelectItem value="ALL">ALL</SelectItem>
                        </SelectContent>
                      </Select>
                      <span className="text-sm font-semibold text-greyscale-text-title">
                        of the following rules
                      </span>
                    </div>

                    {/* Rule rows */}
                    <div className="flex flex-col gap-sm mt-sm">
                      {rules.map((rule, index) => (
                        <div key={rule.id}>
                          {index > 0 && (
                            <p className="text-sm font-semibold text-greyscale-text-caption text-center mb-sm">
                              OR
                            </p>
                          )}
                          <div className="flex items-end gap-xxs">
                            {/* Field */}
                            <div className="flex-1">
                              <Select
                                value={rule.field || undefined}
                                onValueChange={(v) =>
                                  updateRule(rule.id, { field: v })
                                }
                              >
                                <SelectTrigger>
                                  <SelectValue placeholder="Select field" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="salary">Salary</SelectItem>
                                  <SelectItem value="department">Department</SelectItem>
                                  <SelectItem value="branch">Branch</SelectItem>
                                  <SelectItem value="job-title">Job Title</SelectItem>
                                  <SelectItem value="status">Status</SelectItem>
                                </SelectContent>
                              </Select>
                              {index === 0 && (
                                <p className="text-xs text-greyscale-text-caption mt-xxxs">
                                  Field
                                </p>
                              )}
                            </div>

                            {/* Rule */}
                            <div className="flex-1">
                              <Select
                                value={rule.rule || undefined}
                                onValueChange={(v) =>
                                  updateRule(rule.id, { rule: v })
                                }
                              >
                                <SelectTrigger>
                                  <SelectValue placeholder="Select rule" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="equals">Equals</SelectItem>
                                  <SelectItem value="not-equals">Not Equals</SelectItem>
                                  <SelectItem value="contains">Contains</SelectItem>
                                  <SelectItem value="greater-than">Greater Than</SelectItem>
                                  <SelectItem value="less-than">Less Than</SelectItem>
                                </SelectContent>
                              </Select>
                              {index === 0 && (
                                <p className="text-xs text-greyscale-text-caption mt-xxxs">
                                  Rule
                                </p>
                              )}
                            </div>

                            {/* Value */}
                            <div className="flex-1">
                              <InputField
                                placeholder="Enter value"
                                value={rule.value}
                                onChange={(e) =>
                                  updateRule(rule.id, {
                                    value: e.target.value,
                                  })
                                }
                              />
                              {index === 0 && (
                                <p className="text-xs text-greyscale-text-caption mt-xxxs">
                                  Value
                                </p>
                              )}
                            </div>

                            {/* Add / Remove */}
                            <div className="flex items-center gap-xxs mb-[2px]">
                              <Button size="sm" onClick={addRule}>
                                Add
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                disabled={rules.length <= 1}
                                onClick={() => removeRule(rule.id)}
                              >
                                Remove
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Estimated match count */}
                    <div className="flex items-center gap-xxs mt-sm rounded-lg bg-neutral-50 px-md py-sm">
                      <UsersIcon
                        size={16}
                        className="text-greyscale-text-caption"
                      />
                      <span className="text-sm text-greyscale-text-caption">
                        Complete the rules to see estimated match count
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="border-t border-greyscale-border mt-lg" />
              <div className="flex items-center justify-end gap-xxs py-lg">
                <Button variant="outline" onClick={resetSegmentForm}>
                  Cancel
                </Button>
                <Button>Save Segment</Button>
              </div>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-xxs mt-md mb-md">
                <div className="flex-1 max-w-[500px]">
                  <SearchInput
                    placeholder="Search segments by name or description..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onClear={() => setSearch("")}
                  />
                </div>
                <div className="ml-auto flex items-center gap-xxs">
                  <Button variant="outline" leftIcon={<History size={16} />}>
                    View History Updates
                  </Button>
                  <Button
                    leftIcon={<Plus size={16} />}
                    onClick={() => setShowSegmentForm(true)}
                  >
                    Create New Segment
                  </Button>
                </div>
              </div>
              <DataTable
                columns={segmentationColumns}
                data={segmentationData}
                showPagination
                showToolbar={false}
              />
            </>
          )}
        </TabsContent>

        {/* ── Imports tab ── */}
        <TabsContent value="imports">
          <div className="mt-md">
            <DataTable
              columns={importsColumns}
              data={importsData}
              showPagination
              showToolbar={false}
            />
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
